import React from 'react';
import type { FieldValidator } from 'final-form';
import { Field, FieldRenderProps, FieldProps } from 'react-final-form';
import InputFieldV2Adapter from '../InputFieldV2Adapter/InputFieldV2Adapter';
import transformValidateProp from '../../util/transformValidateProp';

export interface FormInputProps<
  RP extends FieldRenderProps<string, T>,
  T extends HTMLElement = HTMLElement
> extends Omit<FieldProps<string, RP, T>, 'validate' | 'type'> {
  name: string;
  validate?: FieldValidator<string>[] | FieldValidator<string> | null;
}

/**
 *
 * ```import FormInput from '@zola/zola-ui/src/components/Form/InputFieldV2/FormField';```
 *
 * Renders a text, password, tel, etc in React Final Form using the Design System.
 *
 * This is a wrapper for the `<input type=` HTML element so it can render
 * passwords, text, or other fields that are `<input>` elements.  By default,
 * it will render a text box.
 *
 * @see InputFieldV2Adapter
 */
const FormInput = <RP extends FieldRenderProps<string, T>, T extends HTMLElement = HTMLElement>(
  props: FormInputProps<RP, T>
): React.ReactElement => {
  const { validate, type = 'text', ...rest } = props;
  const composedValidators = transformValidateProp(validate);

  return (
    <Field validate={composedValidators} type={type} {...rest}>
      {(props) => <InputFieldV2Adapter {...props} />}
    </Field>
  );
};

export default FormInput;
